/**
 * Feed-account detection by account type name.
 *
 * A "feed account" is one that participates in the bank-feed / statement-import
 * / clearing-and-matching system. Feed-ness is identified solely by account
 * type name (`financial_account_types.type_name`) — there is no `is_bank_account`
 * flag on `chart_of_accounts`. Two types are feed accounts:
 *   - `Bank`         (Assets)      — bank / cash accounts
 *   - `Credit Cards` (Liabilities) — credit-card accounts
 *
 * The type is the single source of truth, used consistently across the app
 * (payment forms, `get_cash_balance` — Bank only, the CAMT/bank-feed import UI).
 * Each statement import (`accounting_imports.bank_account_id`) points at a feed
 * account, so "touches a feed account" lines up 1:1 with feed eligibility.
 *
 * These predicates live in `@releve/shared` because both apps need them: the web
 * import UI, and the backend's link guard when pairing an aggregator account to a
 * ledger account. The transaction-shaped helper that consumes them
 * (`transactionTouchesFeedAccount`) stays in `apps/web`, since it depends on the
 * ledger-entry query embeds and has no backend caller.
 */
export const BANK_ACCOUNT_TYPE_NAME = "Bank";
export const CREDIT_CARD_ACCOUNT_TYPE_NAME = "Credit Cards";
/**
 * Account type names that participate in the bank-feed / import / clearing
 * system. Compared case-insensitively via `isFeedAccountType`.
 */
export const FEED_ACCOUNT_TYPE_NAMES = [
    BANK_ACCOUNT_TYPE_NAME,
    CREDIT_CARD_ACCOUNT_TYPE_NAME,
];
/**
 * True when an account type name is a feed account (Bank or Credit Cards).
 * Case-insensitive so it tolerates minor casing differences in stored data.
 */
export function isFeedAccountType(typeName) {
    if (!typeName)
        return false;
    const normalized = typeName.toLowerCase();
    return FEED_ACCOUNT_TYPE_NAMES.some((name) => name.toLowerCase() === normalized);
}
/**
 * True when an account type name is the Credit Cards type. Case-insensitive,
 * mirroring `isFeedAccountType` — use this instead of hand-rolling
 * `typeName.toLowerCase() === CREDIT_CARD_ACCOUNT_TYPE_NAME.toLowerCase()`.
 */
export function isCreditCardType(typeName) {
    if (!typeName)
        return false;
    return typeName.toLowerCase() === CREDIT_CARD_ACCOUNT_TYPE_NAME.toLowerCase();
}
//# sourceMappingURL=feedAccountType.js.map